import { useInfiniteQuery } from '@tanstack/react-query';

import { useSecretTokenStore } from '@/stores/secretToken';
import type { GetClickDetailsResponse, ApiError } from '@/types/api';

const API_BASE_URL = '/api';

class ClickDetailsError extends Error {
  constructor(
    message: string,
    public code: string
  ) {
    super(message);
    this.name = 'ClickDetailsError';
  }
}

interface FetchClickDetailsParams {
  secretToken: string;
  linkId: string;
  cursor?: string | undefined;
  limit?: number;
}

async function fetchClickDetails({
  secretToken,
  linkId,
  cursor,
  limit = 20,
}: FetchClickDetailsParams): Promise<GetClickDetailsResponse> {
  const params = new URLSearchParams();
  if (cursor) {
    params.set('cursor', cursor);
  }
  params.set('limit', String(limit));

  const url = `${API_BASE_URL}/manage/${secretToken}/links/${linkId}/clicks${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await fetch(url);

  if (!response.ok) {
    const errorData = (await response.json()) as ApiError;
    throw new ClickDetailsError(errorData.error.message, errorData.error.code);
  }

  return response.json() as Promise<GetClickDetailsResponse>;
}

interface UseClickDetailsOptions {
  linkId: string;
  enabled?: boolean;
  limit?: number;
}

export function useClickDetails({
  linkId,
  enabled = true,
  limit = 20,
}: UseClickDetailsOptions): ReturnType<
  typeof useInfiniteQuery<GetClickDetailsResponse, ClickDetailsError>
> {
  const { secretToken } = useSecretTokenStore();

  return useInfiniteQuery({
    queryKey: ['clickDetails', secretToken, linkId],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) => {
      if (!secretToken) {
        throw new ClickDetailsError(
          'Secret token is required',
          'MISSING_SECRET_TOKEN'
        );
      }
      return fetchClickDetails({
        secretToken,
        linkId,
        cursor: pageParam,
        limit,
      });
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    initialPageParam: undefined as string | undefined,
    enabled: enabled && !!secretToken && !!linkId,
  });
}

export { ClickDetailsError };
